import { useCallback } from 'react'

import type { one_to_one } from '@fireworkhq/proto-contract'
import { useAutomatedMessageContext } from '@liveHelper/components/AutomatedMessageProvider'
import { useAppointment } from '@liveHelper/context/AppointmentContext/AppointmentContext'
import { useIsOffline } from '@liveHelper/hooks/useIsOffline'
import {
  callDataSelector,
  cameraEnabledSelector,
  conversationSelector,
  embedInstanceIdSelector,
  microphoneMutedSelector,
  paramsSelector,
  useStore,
  useStoreApi,
} from '@liveHelper/models/store'
import { customLiveHelperEvent } from '@liveHelper/tracking/customLiveHelperEvent'
import {
  ACCEPT_INCOMING_CALL,
  AUTOMATED_MESSAGE_IMPRESSION,
  CALENDLY_DATE_AND_TIME_SELECTED,
  CALENDLY_EVENT_SCHEDULED,
  CALENDLY_EVENT_TYPE_VIEWED,
  CALL_AGENT,
  CALL_PROGRESS,
  CLICK_AUTOMATED_MESSAGE,
  CLICK_ENABLE_MICROPHONE,
  CLICK_SCHEDULE_LINK_OUT,
  CLICK_START_CALL,
  CLICK_START_CHAT,
  CLICK_WIDGET,
  CONVERSATION_ACTION,
  DECLINE_INCOMING_CALL,
  DISMISS_AUTOMATED_MESSAGE,
  EMBED_IMPRESSION,
  EMBED_INSTANCE,
  ENABLE_MICROPHONE_IMPRESSION,
  IDLE_IMPRESSION,
  INCOMING_CALL_IMPRESSION,
  JOIN_CONVERSATION,
  LEAD_CAPTURE_EMAIL_IMPRESSION,
  LEAD_CAPTURE_EMAIL_SUBMIT,
  LEAD_CAPTURE_FIRST_NAME_IMPRESSION,
  LEAD_CAPTURE_FIRST_NAME_SUBMIT,
  NAVIGATE_ON_CALL,
  NETWORK_INFO,
  RECEIVE_AI_MESSAGE,
  SEND_MESSAGE,
  START_MONITOR,
  STOP_CALL_AGENT,
  SUBMIT_CUSTOMER_SURVEY,
  SUBMIT_CUSTOMER_SURVEY_FEEDBACK,
  SUBMIT_CUSTOMER_SURVEY_RATING,
  VISITOR_JOIN_FAILED,
  VISITOR_JOIN_SUCCEEDED,
} from '@liveHelper/tracking/endpoints'
import { EventType } from '@liveHelper/tracking/eventType'

import { useCheckCameraPermission } from '@core/devicePermissions/useCheckCameraPermission'
import { useCheckMicrophonePermission } from '@core/devicePermissions/useCheckMicrophonePermission'
import { useNetworkInformation } from '@core/hooks/useNetworkInformation'
import { pruneNilValues } from '@core/object'
import { sendPixelTrackingWithSession as sendPixelTrackingWithSessionFn } from '@core/tracking/senders'
import type {
  ISendPixelTracking,
  PixelTrackingEndpoint,
} from '@core/tracking/types'

import type {
  CONVERSATION_ACTION_TYPE,
  RECEIVE_AI_MESSAGE_TYPE,
  WidgetClickTarget,
} from './types'

function useTracking(endpoint: PixelTrackingEndpoint) {
  const store = useStoreApi()
  const isOffline = useIsOffline()
  const { appointment } = useAppointment()

  return useCallback(
    (payload?: Record<string, unknown>) => {
      const params = paramsSelector(store.getState())
      const embedInstanceId = embedInstanceIdSelector(store.getState())
      const callData = callDataSelector(store.getState())
      const conversation = conversationSelector(store.getState())

      const sendPixelTrackingWithSession = (
        args: Omit<ISendPixelTracking, 'host'>,
      ) => {
        sendPixelTrackingWithSessionFn({
          ...args,
          host: params?.api_host_tracking,
        })
      }

      const pixelData = {
        event_properties: pruneNilValues({
          _chat_channel_id: params?.chat_channel_id,
          embed_instance_id: embedInstanceId,
          _call_id: callData?.id,
          _conversation_id: conversation?.id,
          conversation_intent: conversation?.intent,
          _agent_id: callData?.agentId || conversation?.agent?.id,
          offline: isOffline,
          _appointment_id: appointment?.id,
          ...payload,
        }),
      }

      sendPixelTrackingWithSession({
        endpoint,
        data: pixelData,
      })
    },
    [endpoint, isOffline, appointment?.id, store],
  )
}

/**
 * @returns Tracking used for tracking of the widget's instance
 */
export function useTrackEmbedInstance() {
  const store = useStoreApi()
  const triggerTracking = useTracking(EMBED_INSTANCE)

  return useCallback(() => {
    triggerTracking({
      params: paramsSelector(store.getState()),
    })
  }, [store, triggerTracking])
}

/**
 * @returns Tracking function used for tracking of the widget's impression
 */
export function useTrackEmbedImpression() {
  const triggerTracking = useTracking(EMBED_IMPRESSION)
  const store = useStoreApi()

  return useCallback(() => {
    triggerTracking()
    const params = paramsSelector(store.getState())
    customLiveHelperEvent({
      type: EventType.FW_LIVE_HELPER_IMPRESSION,
      data: { widget_id: params.widget_id },
    })
  }, [triggerTracking, store])
}

/**
 * @returns Tracking function used for tracking of the widget's idle impression
 */
export function useTrackIdleImpression() {
  return useTracking(IDLE_IMPRESSION)
}

/**
 * @returns Tracking function used for tracking of the visitor successfully joined the visitor private channel.
 */
export function useTrackVisitorJoinSucceeded() {
  const triggerTracking = useTracking(VISITOR_JOIN_SUCCEEDED)
  return useCallback(
    (payload: { waitlist_capacity_reached: boolean }) => {
      triggerTracking(payload)
    },
    [triggerTracking],
  )
}

/**
 * @returns Tracking function used for tracking of the visitor failing to join the visitor private channel.
 */
export function useTrackVisitorJoinFailed() {
  const triggerTracking = useTracking(VISITOR_JOIN_FAILED)

  return useCallback(
    (payload?: { reason: string }) => {
      triggerTracking(payload)
    },
    [triggerTracking],
  )
}

/**
 * @returns Tracking function used for tracking of the visitor being monitored
 */
export function useTrackStartMonitor() {
  return useTracking(START_MONITOR)
}

interface useTrackClickWidgetParams {
  click_target: WidgetClickTarget
  last_unread_message?: string
}
/**
 * @returns Tracking function used for tracking of the widget being clicked
 * when it is idle_minimized or when it has message preview
 */
export function useTrackClickWidget() {
  const triggerTracking = useTracking(CLICK_WIDGET)
  const store = useStoreApi()

  return useCallback(
    ({ click_target, last_unread_message }: useTrackClickWidgetParams) => {
      triggerTracking({
        click_target,
        last_unread_message,
      })
      const params = paramsSelector(store.getState())
      customLiveHelperEvent({
        type: EventType.FW_LIVE_HELPER_CLICK_WIDGET,
        data: { widget_id: params.widget_id, click_target },
      })
    },
    [triggerTracking, store],
  )
}

/**
 * @returns Tracking function used for tracking of the visitor calling the agent
 */
export function useTrackCallAgent() {
  return useTracking(CALL_AGENT)
}

/**
 * @returns Tracking function used for tracking of the visitor stop calling the agent
 */
export function useTrackStopCallAgent() {
  return useTracking(STOP_CALL_AGENT)
}

/**
 * @returns Tracking function used for tracking of the incoming call impression
 */
export function useTrackIncomingCallImpression() {
  return useTracking(INCOMING_CALL_IMPRESSION)
}

/**
 * @returns Tracking function used for tracking of the visitor accepting the incoming call
 */
export function useTrackAcceptIncomingCall() {
  return useTracking(ACCEPT_INCOMING_CALL)
}

/**
 * @returns Tracking function used for tracking of the visitor declining the incoming call
 */
export function useTrackDeclineIncomingCall() {
  return useTracking(DECLINE_INCOMING_CALL)
}

/**
 * @returns Tracking function to track joining conversation.
 */
export function useTrackJoinConversation() {
  const triggerTracking = useTracking(JOIN_CONVERSATION)

  return useCallback(
    (payload: {
      conversation: one_to_one.shared.Conversation
      visitorInitiated: boolean
    }) => {
      triggerTracking({
        visitor_initiated: payload.visitorInitiated,
        _conversation_id: payload.conversation.id,
      })
    },
    [triggerTracking],
  )
}

/**
 * @returns Tracking function used for tracking of the visitor customer survey response
 */
export function useTrackSubmitCustomerSurvey() {
  const triggerTracking = useTracking(SUBMIT_CUSTOMER_SURVEY)
  const store = useStoreApi()

  return useCallback(
    (payload?: {
      rating: number | undefined
      feedback: string
      _conversation_id: string | undefined
      intention: 'submit' | 'close'
    }) => {
      triggerTracking(payload)
      if (payload?.intention === 'submit') {
        const params = paramsSelector(store.getState())
        customLiveHelperEvent({
          type: EventType.FW_LIVE_HELPER_SUBMIT_CUSTOMER_SURVEY,
          data: {
            widget_id: params.widget_id,
            rating: payload?.rating,
            feedback: payload?.feedback,
          },
        })
      }
    },
    [triggerTracking, store],
  )
}

/**
 * @returns Tracking function to track customer survey rating.
 */
export function useTrackSubmitCustomerSurveyRating() {
  const triggerTracking = useTracking(SUBMIT_CUSTOMER_SURVEY_RATING)

  return useCallback(
    (payload: {
      rating: number | undefined
      _conversation_id: string | undefined
    }) => {
      triggerTracking(payload)
    },
    [triggerTracking],
  )
}

/**
 * @returns Tracking function to track customer survey feedback.
 */
export function useTrackSubmitCustomerSurveyFeedback() {
  const triggerTracking = useTracking(SUBMIT_CUSTOMER_SURVEY_FEEDBACK)
  const store = useStoreApi()

  return useCallback(
    (payload: {
      feedback: string
      rating: number
      _conversation_id: string | undefined
    }) => {
      triggerTracking(payload)
      const params = paramsSelector(store.getState())
      customLiveHelperEvent({
        type: EventType.FW_LIVE_HELPER_SUBMIT_CUSTOMER_SURVEY,
        data: {
          widget_id: params.widget_id,
          rating: payload?.rating,
          feedback: payload?.feedback,
        },
      })
    },
    [triggerTracking, store],
  )
}

/**
 * @returns Tracking function to track visitor sending a message.
 */
export function useTrackSendMessage() {
  const triggerTracking = useTracking(SEND_MESSAGE)

  return useCallback(
    (payload: {
      messageId: string | undefined
      content: string | undefined
    }) => {
      triggerTracking({
        message_id: payload.messageId,
        content: payload.content,
      })
    },
    [triggerTracking],
  )
}

/**
 * @returns Tracking function to track visitor navigating while on call.
 */
export function useTrackNavigateOnCall() {
  const triggerTracking = useTracking(NAVIGATE_ON_CALL)
  const store = useStoreApi()

  return useCallback(
    (conversationId: string | undefined) => {
      triggerTracking({
        _conversation_id: conversationId,
      })
      const params = paramsSelector(store.getState())
      customLiveHelperEvent({
        type: EventType.FW_LIVE_HELPER_NAVIGATE,
        data: {
          widget_id: params.widget_id,
          page_url: window.location.href,
        },
      })
    },
    [triggerTracking, store],
  )
}

/**
 * @returns Tracking function to track call progress
 */
export function useTrackCallProgress() {
  const triggerTracking = useTracking(CALL_PROGRESS)
  const { checkPermissionStatus: checkMicrophonePermissionStatus } =
    useCheckMicrophonePermission({})
  const { checkPermissionStatus: checkCameraPermissionStatus } =
    useCheckCameraPermission({})
  const fetchNetworkInfo = useNetworkInformation()

  const microphoneMuted = useStore(microphoneMutedSelector)
  const cameraEnabled = useStore(cameraEnabledSelector)

  return useCallback(async () => {
    const microphonePermission = await checkMicrophonePermissionStatus()
    const cameraPermission = await checkCameraPermissionStatus()

    triggerTracking({
      microphone_permission: microphonePermission,
      microphone_muted: microphoneMuted,
      camera_permission: cameraPermission,
      camera_enabled: cameraEnabled,
      network_info: fetchNetworkInfo(),
    })
  }, [
    checkMicrophonePermissionStatus,
    checkCameraPermissionStatus,
    triggerTracking,
    microphoneMuted,
    cameraEnabled,
    fetchNetworkInfo,
  ])
}

/**
 * @returns Tracking function to track click on schedule link out button
 */
export function useTrackClickScheduleLinkOut() {
  return useTracking(CLICK_SCHEDULE_LINK_OUT)
}

/**
 * @returns Tracking function to track enable microphone screen impression
 */
export function useTrackEnableMicrophoneImpression() {
  return useTracking(ENABLE_MICROPHONE_IMPRESSION)
}

/**
 * @returns Tracking function to track clicks on enable microphone screen
 */
export function useTrackClickEnableMicrophone() {
  const triggerTracking = useTracking(CLICK_ENABLE_MICROPHONE)

  return useCallback(
    (payload?: { enableNow: boolean }) => {
      triggerTracking({
        enable_now: payload?.enableNow,
      })
    },
    [triggerTracking],
  )
}

/**
 * @returns Tracking function to track click on start call button
 */
export function useTrackClickStartCall() {
  return useTracking(CLICK_START_CALL)
}

/**
 * @returns Tracking function to track click on start chat button
 */
export function useTrackClickStartChat() {
  return useTracking(CLICK_START_CHAT)
}

/**
 * @returns Tracking function to track calendly event type viewed
 */
export function useTrackCalendlyEventTypeViewed() {
  return useTracking(CALENDLY_EVENT_TYPE_VIEWED)
}

/**
 * @returns Tracking function to track calendly date and time selected
 */
export function useTrackCalendlyDateAndTimeSelected() {
  return useTracking(CALENDLY_DATE_AND_TIME_SELECTED)
}

/**
 * @returns Tracking function to track calendly event scheduled
 */
export function useTrackCalendlyEventScheduled() {
  const triggerTracking = useTracking(CALENDLY_EVENT_SCHEDULED)

  return useCallback(
    (payload?: { calendly: unknown }) => {
      triggerTracking(payload)
    },
    [triggerTracking],
  )
}

/** @returns Tracking function to track automated message impression */
export function useTrackAutomatedMessageImpression() {
  const { automatedMessage } = useAutomatedMessageContext()
  const triggerTracking = useTracking(AUTOMATED_MESSAGE_IMPRESSION)
  return useCallback(() => {
    triggerTracking({
      automated_message: automatedMessage,
    })
  }, [triggerTracking, automatedMessage])
}

/** @returns Tracking function to track click on automated message */
export function useTrackClickAutomatedMessage() {
  const { automatedMessage } = useAutomatedMessageContext()
  const store = useStoreApi()
  const triggerTracking = useTracking(CLICK_AUTOMATED_MESSAGE)
  return useCallback(() => {
    const params = paramsSelector(store.getState())
    triggerTracking({
      automated_message: automatedMessage,
    })
    customLiveHelperEvent({
      type: EventType.FW_LIVE_HELPER_CLICK_WIDGET,
      data: {
        widget_id: params.widget_id,
        click_target: 'automated_message',
      },
    })
  }, [store, triggerTracking, automatedMessage])
}

/** @returns Tracking function to track dismiss automated message */
export function useTrackDismissAutomatedMessage() {
  const { automatedMessage } = useAutomatedMessageContext()
  const triggerTracking = useTracking(DISMISS_AUTOMATED_MESSAGE)
  return useCallback(() => {
    triggerTracking({
      automated_message: automatedMessage,
    })
  }, [triggerTracking, automatedMessage])
}

/** @returns Tracking function to track lead capture first name impression */
export function useTrackLeadCaptureFirstNameImpression() {
  return useTracking(LEAD_CAPTURE_FIRST_NAME_IMPRESSION)
}

/** @returns Tracking function to track lead capture first name submit */
export function useTrackLeadCaptureFirstNameSubmit() {
  const triggerTracking = useTracking(LEAD_CAPTURE_FIRST_NAME_SUBMIT)

  return useCallback(
    (payload: { first_name: string }) => {
      triggerTracking(payload)
    },
    [triggerTracking],
  )
}

/** @returns Tracking function to track lead capture email impression */
export function useTrackLeadCaptureEmailImpression() {
  return useTracking(LEAD_CAPTURE_EMAIL_IMPRESSION)
}

/** @returns Tracking function to track lead capture email submit */
export function useTrackLeadCaptureEmailSubmit() {
  const triggerTracking = useTracking(LEAD_CAPTURE_EMAIL_SUBMIT)

  return useCallback(
    (payload: { email: string }) => {
      triggerTracking(payload)
    },
    [triggerTracking],
  )
}

/**  @returns Tracking function to track network info */
export function useTrackNetworkInfo() {
  const triggerTracking = useTracking(NETWORK_INFO)
  const fetchNetworkInfo = useNetworkInformation()

  return useCallback(() => {
    triggerTracking({ network_info: fetchNetworkInfo() })
  }, [fetchNetworkInfo, triggerTracking])
}

/**
 * @returns Tracking function to track receiving an message from AI.
 */
export function useTrackReceiveAIMessage() {
  const triggerTracking = useTracking(RECEIVE_AI_MESSAGE)

  return useCallback(
    (payload: {
      type: RECEIVE_AI_MESSAGE_TYPE
      messageId: string | undefined
      content: string | undefined
    }) => {
      triggerTracking({
        type: payload.type,
        message_id: payload.messageId,
        content: payload.content,
      })
    },
    [triggerTracking],
  )
}

/**
 * @returns Tracking function to track visitor clicking on an action in a conversation.
 */
export function useTrackConversationAction() {
  const triggerTracking = useTracking(CONVERSATION_ACTION)

  return useCallback(
    (payload: {
      type: RECEIVE_AI_MESSAGE_TYPE
      action: CONVERSATION_ACTION_TYPE
      content: string | undefined
    }) => {
      triggerTracking({
        type: payload.type,
        action: payload.action,
        content: payload.content,
      })
    },
    [triggerTracking],
  )
}
